import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { socialAuthApi } from '../../../api';
import TwoFactorAuthVerification from '../../../components/TwoFactorAuthVerification';
import { AuthFormData, FormErrors } from '../types';
import { ApiError } from '../../../api/types';

interface LoginFormProps {
  onForgotPassword: () => void;
  onEmailVerificationRequired?: (email: string) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onForgotPassword, onEmailVerificationRequired }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<AuthFormData>({
    email: '',
    identifier: '', // Can be email, phone, or username
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [socialLoading, setSocialLoading] = useState<'google' | 'facebook' | null>(null);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState<'google_authenticator' | 'sms' | null>(null);
  const [pendingIdentifier, setPendingIdentifier] = useState('');
  const [pendingPassword, setPendingPassword] = useState('');

  // Get redirect path from location state or default to timeline
  const from = (location.state as any)?.from?.pathname || '/timeline';

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const identifier = formData.identifier || formData.email; // Support both for backward compatibility

    if (!identifier.trim()) {
      newErrors.email = t('form.required');
    }

    if (!formData.password) {
      newErrors.password = t('form.required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const identifier = formData.identifier || formData.email; // Support both for backward compatibility
      const result = await login(identifier, formData.password);
      
      if (result?.requires_2fa_setup && result?.user) {
        const u = result.user as any;
        const editPath = (u.uuid && u.slug)
          ? `/user/${u.uuid}-${u.slug}/edit?tab=account&setup_2fa=true`
          : `/user/${u.username || u.id}/edit?tab=account&setup_2fa=true`;
        toast.warning(t('account.two_factor.required') || 'Two-factor authentication is required for your account. Please complete setup.', {
          position: 'top-center',
          autoClose: 10000,
        });
        navigate(editPath, { replace: true });
        return;
      }
      
      toast.success(t('auth.login_success') || 'Login successful! Welcome back.', {
        position: 'top-center',
        autoClose: 10000,
      });
      navigate(from, { replace: true });
    } catch (error: any) {
      // Check if 2FA is required
      if (error?.requires_2fa === true) {
        setPendingIdentifier(formData.identifier || formData.email);
        setPendingPassword(formData.password);
        setTwoFactorMethod(error?.method || 'google_authenticator');
        setRequires2FA(true);
        setIsLoading(false);
        return;
      }

      // Check if email verification is required (check both direct property and message content as fallback)
      const requiresVerification = error?.requires_verification === true || 
                                   error?.message?.includes('verify your email');
      
      if (requiresVerification && onEmailVerificationRequired) {
        const identifier = formData.identifier || formData.email;
        // Extract email from identifier if it's an email, otherwise use as-is
        const email = identifier.includes('@') ? identifier : identifier;
        onEmailVerificationRequired(email);
        return;
      }
      
      // Handle rate limiting error (429)
      if (error?.response?.status === 429) {
        const rateLimitMessage = t('auth.rate_limit_exceeded') || 'Too many login attempts. Please wait a minute and try again.';
        setErrors({ form: rateLimitMessage });
        toast.error(rateLimitMessage, {
          position: 'top-center',
          autoClose: 10000,
        });
      } else {
        // Extract error message from validation errors if available
        let errorMessage = error?.message || error?.response?.data?.message;
        
        // Check if there are validation errors with specific field messages
        if (error?.errors) {
          // Check for identifier/password/email errors (credentials errors)
          const credentialErrors = error.errors.identifier || error.errors.password || error.errors.email;
          if (credentialErrors && credentialErrors.length > 0) {
            errorMessage = credentialErrors[0];
          } else if (error.errors.form && error.errors.form.length > 0) {
            errorMessage = error.errors.form[0];
          } else {
            // Get first error from any field
            const firstErrorKey = Object.keys(error.errors)[0];
            if (firstErrorKey && error.errors[firstErrorKey]?.length > 0) {
              errorMessage = error.errors[firstErrorKey][0];
            }
          }
        }
        
        // Fallback to translation or default message
        if (!errorMessage || errorMessage === 'Invalid input provided' || errorMessage === 'Validation failed') {
          errorMessage = t('auth.invalid_credentials') || 'Invalid email or password';
        }
        
        setErrors({ form: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAVerify = async (code: string, recoveryCode?: string) => {
    setIsLoading(true);
    try {
      const result = await login(pendingIdentifier, pendingPassword, code, recoveryCode);
      if (result?.requires_2fa_setup && result?.user) {
        const u = result.user as any;
        const editPath = (u.uuid && u.slug)
          ? `/user/${u.uuid}-${u.slug}/edit?tab=account&setup_2fa=true`
          : `/user/${u.username || u.id}/edit?tab=account&setup_2fa=true`;
        toast.warning(t('account.two_factor.required') || 'Two-factor authentication is required for your account. Please complete setup.', {
          position: 'top-center',
          autoClose: 10000,
        });
        navigate(editPath, { replace: true });
        return;
      }
      toast.success(t('auth.login_success') || 'Login successful! Welcome back.');
      navigate(from, { replace: true });
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.two_factor_code?.[0]
        || error?.response?.data?.message
        || error?.message
        || 'Invalid verification code';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendSms = async () => {
    // SMS code should already be sent by backend, but we can trigger a resend if needed
    toast.info('SMS code sent. Please check your phone.');
  };

  const handleGoogleLogin = async () => {
    setSocialLoading('google');
    try {
      const response = await socialAuthApi.getGoogleAuthUrl();
      // Response is now { auth_url: "..." } directly
      if (!response.auth_url) {
        throw new Error('No auth URL returned from server');
      }
      window.location.href = response.auth_url;
    } catch (error: any) {
      console.error('Google login error:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to initiate Google login');
      setSocialLoading(null);
    }
  };

  const handleFacebookLogin = async () => {
    setSocialLoading('facebook');
    try {
      const response = await socialAuthApi.getFacebookAuthUrl();
      // Response is now { auth_url: "..." } directly
      if (!response.auth_url) {
        throw new Error('No auth URL returned from server');
      }
      window.location.href = response.auth_url;
    } catch (error: any) {
      console.error('Facebook login error:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to initiate Facebook login');
      setSocialLoading(null);
    }
  };

  // Show 2FA verification if required
  if (requires2FA && twoFactorMethod) {
    return (
      <div className="space-y-6">
        <TwoFactorAuthVerification
          method={twoFactorMethod}
          onVerify={handle2FAVerify}
          onResendSms={twoFactorMethod === 'sms' ? handleResendSms : undefined}
          loading={isLoading}
        />
        <Button
          type="button"
          variant="outline"
          fullWidth
          onClick={() => {
            setRequires2FA(false);
            setTwoFactorMethod(null);
            setPendingIdentifier('');
            setPendingPassword('');
          }}
        >
          Back to Login
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.form && (
        <div className="p-4 bg-error/10 border border-error rounded-lg flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{errors.form}</p>
        </div>
      )}

      <Input
        type="text"
        label={t('auth.email_or_phone')}
        placeholder={t('auth.email_placeholder')}
        value={formData.identifier || formData.email}
        onChange={(e) => setFormData({ ...formData, identifier: e.target.value, email: e.target.value })}
        error={errors.email}
        required
      />

      <div className="relative">
        <Input
          type={showPassword ? 'text' : 'password'}
          label={t('auth.password')}
          placeholder={t('auth.password_placeholder')}
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          error={errors.password}
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-fast"
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
        </button>
      </div>

      <div className="flex items-center justify-between">
        <Checkbox
          label={t('auth.remember_me')}
          checked={formData.rememberMe}
          onChange={(checked) => setFormData({ ...formData, rememberMe: checked })}
        />
        <button
          type="button"
          onClick={onForgotPassword}
          className="text-sm text-primary hover:text-primary/80 font-medium transition-fast"
        >
          {t('auth.forgot_password')}
        </button>
      </div>

      <Button type="submit" variant="default" fullWidth loading={isLoading} iconName="LogIn" iconPosition="right">
        {t('auth.sign_in')}
      </Button>

      {/* Social Login Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border"></div>
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">
            {t('auth.or_continue_with')}
          </span>
        </div>
      </div>

      {/* Social Login Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          type="button"
          variant="outline"
          fullWidth
          onClick={handleGoogleLogin}
          disabled={isLoading || socialLoading !== null}
          loading={socialLoading === 'google'}
        >
          Google
        </Button>
        <Button
          type="button"
          variant="outline"
          fullWidth
          onClick={handleFacebookLogin}
          disabled={isLoading || socialLoading !== null}
          loading={socialLoading === 'facebook'}
        >
          Facebook
        </Button>
      </div>
    </form>
  );
};

export default LoginForm;